import React, { useEffect, useRef, useState } from 'react';

/*
 * The manual's contents rail. Groups come straight from the section data, so the rail
 * order is the reading order and there is no second list to keep in step.
 *
 * The app scrolls inside <main>, not the window, so the observer that tracks the
 * reader's position has to be rooted on that element rather than the viewport.
 */

function scrollParentOf(node) {
  let el = node?.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  const anchorRef = useRef(null);
  // Re-running on every array identity would tear the observer down each render;
  // the joined ids change only when the visible section list actually changes.
  const key = ids.join('|');

  useEffect(() => {
    // Absent in jsdom, and in any browser old enough to lack it the rail simply
    // stops following the reader rather than taking the page down with it.
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const sections = ids
      .map((id) => document.getElementById(`manual-${id}`))
      .filter(Boolean);
    if (sections.length === 0) return undefined;

    const root = scrollParentOf(anchorRef.current) || null;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActive(visible[0].target.id.replace('manual-', ''));
        }
      },
      // A band across the upper third: whatever the reader is actually looking at,
      // rather than whichever section happens to touch the very top edge.
      { root, rootMargin: '-80px 0px -66% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { active, anchorRef };
}

export default function ManualContents({ groups, active, onSelect, anchorRef }) {
  return (
    <nav ref={anchorRef} aria-label="Manual contents" className="text-sm">
      <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-gray-400">
        Contents
      </div>

      {groups.length === 0 && (
        <p className="text-sm text-gray-500">No sections match your search.</p>
      )}

      {groups.map((group) => (
        <div key={group.name} className="mb-4">
          <div className="mb-1 px-2 font-mono text-[10px] uppercase tracking-wider text-gray-400">
            {group.name}
          </div>
          <ul>
            {group.sections.map((section) => {
              const isActive = section.id === active;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(section.id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={`w-full rounded-md px-2 py-1.5 text-left text-[13px] leading-snug transition-colors ${
                      isActive
                        ? 'bg-cloudza-50 font-semibold text-cloudza-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {section.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}
